import { useMutation, useQuery, useQueryClient } from 'react-query'

import axios from 'config/axios'

import { API_ENDPOINTS } from '../../config/api'
import { ChallengeTemplate, fetchChallengeTemplate } from '../AlgoEditor'

import {
  ActiveUsersResponse,
  AllCodingRoomsData,
  AllCodingRoomsResponse,
  CodingRoomData,
  CodingRoomResponse,
  CreateRoomRequestBody,
  CreateRoomResponse,
  JoinRoomResponse,
  LeaderboardData,
  LeaderboardResponse,
  LeaveRoomResponse
} from './codingRooms.types'

const fetchCreateRoom = async (body: CreateRoomRequestBody) => {
  const response = await axios.post(API_ENDPOINTS.CODING_ROOMS, body)
  const { data } = (await response.data) as CreateRoomResponse

  return {
    room_id: data.attributes.room_details.id
  }
}

export const useCreateRoomMutation = () => {
  return useMutation((body: CreateRoomRequestBody) => fetchCreateRoom(body))
}

const fetchRoom = async (roomId: string) => {
  const response = await axios.get(`${API_ENDPOINTS.CODING_ROOMS}/${roomId}`)
  const { data } = (await response.data) as CodingRoomResponse

  const { challenge, room_details, ...restAttributes } = data.attributes

  const modifiedChallenge = challenge.map(({ difficulty, ...rest }) => ({
    ...rest,
    difficulty: difficulty.replace('_level', '')
  }))

  const modifiedRoomDetails = {
    ...room_details,
    difficulty: JSON.stringify(
      JSON.parse(room_details.difficulty).map((d: string) =>
        d.replace('_level', '')
      )
    )
  }

  return {
    ...restAttributes,
    challenge: modifiedChallenge,
    room_details: modifiedRoomDetails
  }
}

export const useStartAndGetCodingRoom = (roomId: string) => {
  return useQuery<CodingRoomData | null, Error>(
    ['coding-rooms', { roomId }],
    () => fetchRoom(roomId),
    {
      enabled: !!roomId,
      onError: (error: Error) => {
        return error
      }
    }
  )
}

export const fetchCodingRoomWithAllQuestionTemplate = async (
  roomId: string
) => {
  const roomData = await fetchRoom(roomId)
  const challengesIds = roomData.challenge.map((chall) => chall.id)

  const allTemplates = await Promise.all(
    challengesIds.map((id) => fetchChallengeTemplate(id))
  )

  const templates = challengesIds.map((id, idx) => {
    return {
      id,
      template: allTemplates[idx]
    }
  })

  return { roomData, templates }
}

export const useCodingRoomWithAllQuestionTemplate = (roomId: string) => {
  return useQuery<
    {
      roomData: CodingRoomData
      templates: {
        id: number
        template: ChallengeTemplate[]
      }[]
    },
    Error
  >(
    ['coding-rooms-with-template', { roomId }],
    () => fetchCodingRoomWithAllQuestionTemplate(roomId),
    {
      enabled: !!roomId,
      onError: (error: Error) => {
        return error
      }
    }
  )
}

export const fetchPaginatedCodingRoomsLeaderboard = async (
  roomId: number,
  currentPage: number,
  pageSize: number
) => {
  const response = await axios.get(
    `${API_ENDPOINTS.CODING_ROOMS_LEADERBOARD}?id=${roomId}&page=${currentPage}&size=${pageSize}`
  )
  const { data } = (await response.data) as LeaderboardResponse

  return {
    ...data.attributes
  }
}

export const usePaginatedCodingRoomsLeaderboard = (
  roomId: number,
  currentPage: number,
  pageSize: number,
  poll: boolean
) => {
  return useQuery<LeaderboardData, null>(
    ['coding-rooms-leaderboard', roomId, currentPage, pageSize],
    () => fetchPaginatedCodingRoomsLeaderboard(roomId, currentPage, pageSize),
    {
      refetchInterval: poll ? 5000 : false,
      enabled: !!(roomId && currentPage && pageSize)
    }
  )
}

const fetchLeaveRoom = async (roomId: string) => {
  const response = await axios.put(
    `${API_ENDPOINTS.CODING_ROOMS_LEAVE_ROOM}?coding_room_id=${roomId}`
  )
  const { data } = (await response.data) as LeaveRoomResponse

  return { message: data.attributes.message }
}

export const useLeaveRoom = (roomId: string) => {
  const queryClient = useQueryClient()
  return useMutation(fetchLeaveRoom, {
    onSuccess: () => {
      queryClient.invalidateQueries(['coding-rooms-all'])
      queryClient.invalidateQueries(['coding-rooms', { roomId }])
    }
  })
}

export async function fetchAllCodingRooms() {
  const response = await axios.get(API_ENDPOINTS.CODING_ROOMS)
  const { data } = (await response.data) as AllCodingRoomsResponse

  const {
    active_user_room: activeRooms,
    coding_rooms: allRooms,
    user_coding_room: userRooms,
    ...restAttributes
  } = data.attributes

  const modifiedActiveRooms = activeRooms.map(({ difficulty, ...rest }) => ({
    ...rest,
    difficulty: JSON.stringify(
      JSON.parse(difficulty).map((d: string) => d.replace('_level', ''))
    )
  }))

  const modifiedUserRooms = userRooms.map(({ difficulty, ...rest }) => ({
    ...rest,
    difficulty: JSON.stringify(
      JSON.parse(difficulty).map((d: string) => d.replace('_level', ''))
    )
  }))

  const modifiedAllRooms = allRooms.map(({ difficulty, ...rest }) => ({
    ...rest,
    difficulty: JSON.stringify(
      JSON.parse(difficulty).map((d: string) => d.replace('_level', ''))
    )
  }))

  return {
    ...restAttributes,
    active_user_room: modifiedActiveRooms,
    coding_rooms: modifiedAllRooms,
    user_coding_room: modifiedUserRooms
  }
}

export const useAllCodingRooms = () => {
  return useQuery<AllCodingRoomsData, Error>(
    ['coding-rooms-all'],
    fetchAllCodingRooms,
    {
      onError: (error: Error) => {
        return error
      }
    }
  )
}

const fetchJoinCodigRoom = async (uniqueRoomCode: string) => {
  const response = await axios.post(
    `${API_ENDPOINTS.CODING_ROOMS_JOIN}?unique_room_code=${uniqueRoomCode}`
  )
  const { data } = (await response.data) as JoinRoomResponse

  return {
    ...data.attributes
  }
}

export const useJoinRoom = () => {
  return useMutation((uniqueRoomCode: string) =>
    fetchJoinCodigRoom(uniqueRoomCode)
  )
}

const fetchActiveUsers = async (roomId: number) => {
  const response = await axios.get(
    `${API_ENDPOINTS.CODING_ROOMS}/${roomId}/active_user_list`
  )

  const { data } = (await response.data) as ActiveUsersResponse
  return data.attributes.users
}

export const useActiveUsers = (roomId: number) => {
  return useQuery<number, Error>(
    ['coding-rooms-active-users', { roomId }],
    () => fetchActiveUsers(roomId),
    {
      enabled: false,
      onError: (error: Error) => {
        return error
      }
    }
  )
}

type UpdateRoomDetailsProps = {
  roomId: string
  name: string
}

const fetchUpdateRoomDetails = async (props: UpdateRoomDetailsProps) => {
  const { name, roomId } = props

  const response = await axios.put(
    `${API_ENDPOINTS.CODING_ROOMS}/${roomId}/update_room_details`,
    {
      data: {
        attributes: {
          name
        }
      }
    }
  )

  return response
}

export const useUpdateRoomMutation = (roomId: string) => {
  const queryClient = useQueryClient()

  return useMutation(
    (props: UpdateRoomDetailsProps) => fetchUpdateRoomDetails(props),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'coding-rooms-with-template',
          { roomId }
        ])
      }
    }
  )
}
